<template>
  <layout name="Sidebar" extraClass="table-view-layout">
    <VTableView
      :sidebar="{
        buttons,
        title: 'Contacts',
        extraClass: 'contacts-layout-heading'
      }"
      :withPagination="
        (pagination.total_count > 25 && itemsPerPage > 25) ||
          pagination.page_count > 1
      "
    >
      <template slot="search">
        <ContactsFilter />
      </template>

      <template slot="table">
        <ContactsTable extraClass="contact-table" />
      </template>

      <template slot="pagination">
        <ContactsPagination />
      </template>

      <template slot="extra">
        <ContactsCreateModal
          v-if="addContactOpened"
          @contactcreated="closeModal('addContactOpened')"
          @close="closeAddContact"
        >
        </ContactsCreateModal>
      </template>
    </VTableView>
  </layout>
</template>

<script>
import Vue from "vue";
import { mapActions, mapGetters } from "vuex";
import { map } from "lodash";
import Layout from "@/layouts/Layout";

import VTableView from "@/views/VTableView";

// components
import ContactsTable from "@/components/ContactsTable";
import ContactsCreateModal from "@/components/ContactsCreateModal";
import VFlatFileImporter from "@/components/VFlatFileImporter";
import ContactsFilter from "@/components/ContactsFilter";
import ContactsPagination from "@/components/ContactsPagination";

// mixins
import withNavigate from "@/mixins/withNavigate";
import withFormUtils from "@/mixins/withFormUtils";
import withToasted from "@/mixins/withToasted";

import { STATES_SELECT_LIST } from "@/constants/select";

export default Vue.component("Contacts", {
  metaInfo: {
    title: "Contacts"
  },

  components: {
    Layout,
    ContactsTable,
    ContactsCreateModal,
    ContactsFilter,
    ContactsPagination,
    VFlatFileImporter,
    VTableView
  },

  mixins: [withNavigate, withToasted, withFormUtils],

  data() {
    return {
      csvFile: null,
      // modals
      addContactOpened: false,
      csvUploading: false
    };
  },

  methods: {
    _handleFlatFileUploadedContacts(results) {
      this.sendFlatFileUploadResults({
        data: results,
        onSuccess: () => {
          this.toast(
            "Your contacts are on the way! Please allow up to 10 minutes to process large CSVs."
          );
        },
        onError: () => {
          this.toast(
            "Something went wrong during uploading your file. Please, contact support.",
            "error"
          );
        }
      });
    },

    onButtonClick(e, key) {
      if (key === "new-contact") {
        this.addContactOpened = true;
      }
    },

    closeAddContact() {
      this.closeModal("addContactOpened");
    },

    closeModal(param) {
      this[param] = false;
    },

    ...mapActions("contacts", [
      "uploadCsv",
      "changeSearchString",
      "sendFlatFileUploadResults"
    ])
  },

  computed: {
    buttons() {
      return [
        {
          component: VFlatFileImporter,
          props: {
            customer: {
              userId: this.getUser.id,
              companyName: this.getAccount.company,
              email: this.getUser.email
            },
            config: {
              fields: [
                {
                  label: "First Name",
                  key: "first_name",
                  alternates: ["name"],
                  validators: [{ validate: "required" }]
                },
                {
                  label: "Last Name",
                  key: "last_name",
                },
                {
                  label: "Email",
                  key: "email",
                  validators: [
                    {
                      validate: "regex_matches",
                      regex:
                        '^(([^<>()\\[\\]\\\\.,;:\\s@"]+(\\.[^<>()\\[\\]\\\\.,;:\\s@"]+)*)|(".+"))@((\\[[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}\\.[0-9]{1,3}])|(([a-zA-Z\\-0-9]+\\.)+[a-zA-Z]{2,}))$',
                      error: "Invalid email format"
                    }
                  ]
                },
                {
                  label: "Company",
                  key: "company"
                },
                {
                  label: "Address Line 1",
                  key: "address_line_1",
                  alternates: ["Address", "Addr", "Address 1"],
                  validators: [{ validate: "required" }]
                },
                {
                  label: "Address Line 2",
                  alternates: ["Address 2"],
                  key: "address_line_2"
                },
                {
                  label: "City",
                  key: "address_city",
                  validators: [{ validate: "required" }]
                },
                {
                  label: "State/Province",
                  key: "address_state",
                  alternates: ["St", "State code", "Contact State"],
                  validators: [{ validate: "required" }]
                },
                {
                  label: "ZIP",
                  key: "address_zip",
                  alternates: ["Postal", "Zip code", "Contact ZIP"],
                  validators: [{ validate: "required" }]
                },
                {
                  label: "Country",
                  key: "address_country",
                  alternates: ["Co", "Country Code"],
                  validators: [{ validate: "required" }]
                },

                {
                  label: "Phone",
                  key: "phone",
                  alternates: ["Ph", "Mobile"],
                  validators: [
                    {
                      validate: "regex_matches",
                      // regex: "^\\+((?:9[679]|8[035789]|6[789]|5[90]|42|3[578]|2[1-689])|9[0-58]|8[1246]|6[0-6]|5[1-8]|4[013-9]|3[0-469]|2[70]|7|1)(?:\\W*\\d){0,13}\\d$/",
                      error: "Invalid phone number format"
                    }
                  ]
                }
              ]
            },
            onSuccess: this._handleFlatFileUploadedContacts,
            onCancel: () => {},
            recordHookHandler: (record, index, mode) => {
              let out = {};

              // helper for country field
              const country_aliases = [
                "USA",
                "U.S.",
                "America",
                "United States",
                "U.S.A."
              ];

              if (record.address_country.length < 2) {
                out.address_country = {
                  info: [
                    {
                      message: "Country must be at least 2 characters long",
                      level: "error"
                    }
                  ]
                };
              } else if (
                record.address_country.length > 2 &&
                country_aliases.includes(record.address_country)
              ) {
                out.address_country = {
                  value: "US",
                  info: [
                    {
                      message: "Replaced with 2 characters abbreviation",
                      level: "info"
                    }
                  ]
                };
              }
              // } else if (
              //   record.address_country === 2 &&
              //   !record.address_country.match(
              //     /^( A[^ABCHJKNPVY]|B[^CKPUX]|C[^BEJPQST]|D[EJKMOZ]|E[CEGHRST]|F[IJKMOR]|G[^CJKOVXZ]|H[KMNRTU]|I[DEL-OQ-T]|J[EMOP]|K[EGHIMNPRWYZ]|L[ABCIKR-VY]|M[^BIJ]|N[ACEFGILOPRUZ]|OM|P[AE-HK-NRSTWY]|QA|R[EOSUW]|S[^FPQUW]|T[^ABEIPQSUXY]|U[AGMSYZ]|V[ACEGINU]|WF|WS|YE|YT|Z[AMW] )$/
              //   )
              // ) {
              //   out.address_country = {
              //     value: "US",
              //     info: [
              //       {
              //         message: "Replaced with 2 char abbreviation",
              //         level: "info"
              //       }
              //     ]
              //   };
              // }

              let stateToReplace = STATES_SELECT_LIST.find(
                state => state.label.toLowerCase() === record.address_state.toLowerCase()
              );

              /*helper for state field*/
              if (stateToReplace) {
                out.address_state = {
                  value: stateToReplace.value,
                  info: [
                    {
                      message: "Replaced with 2 characters abbreviation",
                      level: "info"
                    }
                  ]
                };
              } 

              return out;
            }
          }
        },
        {
          key: "new-contact",
          type: "primary",
          label: "New Contact",
          size: "regular",
          onClickHandler: e => this.onButtonClick(e, "new-contact"),
          extraClass: "add-contact"
        }
      ];
    },

    ...mapGetters("contacts", ["isLoading", "pagination", "search", "sort"]),
    ...mapGetters("service", ["itemsPerPage"]),
    ...mapGetters("account", ["getUser", "getAccount"])
  },

  destroyed() {
    this.changeSearchString("");
  }
});
</script>

<style lang="scss"></style>
